// src/app/time-attendance/components/payroll-sync/pages/payroll-sync/payroll-sync.component.ts

import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { PayrollSyncService } from '../services/payroll-sync.service';
import { ApiPagedResponse, YywwDto } from '../models/payroll-sync.model';
import { PayrollNavComponent } from '../../../shared/payroll-nav/payroll-nav.component';

type ToastKind = 'success' | 'error' | 'info';

@Component({
    selector: 'app-payroll-sync',
    standalone: true,
    imports: [CommonModule, FormsModule, PayrollNavComponent],
    templateUrl: './payroll-sync.component.html',
    styleUrls: ['./payroll-sync.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayrollSyncComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    isLoading = true;
    errorMessage: string | null = null;
    isSyncing = false;

    toastMessage: string | null = null;
    toastKind: ToastKind = 'info';
    private toastTimer: ReturnType<typeof setTimeout> | null = null;

    selectedYyww: YywwDto | null = null;

    yywwOpen = false;
    yywwSearch = String(new Date().getFullYear());
    yywwResults: YywwDto[] = [];
    yywwIsLoading = false;

    private yywwPage = 1;
    private readonly yywwPageSize = 20;
    private yywwHasMore = true;
    private yywwActiveTerm = '';
    private yywwSeq = 0;

    private readonly yywwSearch$ = new Subject<string>();

    constructor(
        private readonly payrollSyncSvc: PayrollSyncService,
        private readonly cdr: ChangeDetectorRef,
        private readonly hostEl: ElementRef<HTMLElement>,
    ) { }

    ngOnInit(): void {
        this.yywwSearch$
            .pipe(
                takeUntil(this.destroy$),
                map((value) => (value ?? '').trim()),
                debounceTime(1000),
                distinctUntilChanged(),
            )
            .subscribe((term) => {
                void this.yywwResetAndSearch(term, false);
            });

        void this.loadCore();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearToast();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocMouseDown(event: MouseEvent): void {
        if (!this.yywwOpen) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const host = this.hostEl.nativeElement;
        if (!host.contains(target)) {
            this.closeAllDropdowns();
            return;
        }

        const insidePanel = !!target.closest('.combo-panel');
        const insideTrigger = !!target.closest('.combo-input');

        if (!insidePanel && !insideTrigger) {
            this.closeAllDropdowns();
        }
    }

    @HostListener('document:keydown.escape')
    onEscape(): void {
        if (this.yywwOpen) {
            this.closeAllDropdowns();
        }
    }

    async loadCore(): Promise<void> {
        this.isLoading = true;
        this.errorMessage = null;
        this.cdr.markForCheck();

        try {
            await this.loadInitialPreviousWeek();
        } catch (error) {
            console.error(error);
            this.errorMessage = 'Failed to load payroll weeks.';
        } finally {
            this.isLoading = false;
            this.cdr.markForCheck();
        }
    }

    refresh(): void {
        void this.loadCore();
    }

    closeAllDropdowns(): void {
        this.yywwOpen = false;
        this.cdr.markForCheck();
    }

    openYyww(): void {
        this.closeAllDropdowns();
        this.yywwOpen = true;
        this.cdr.markForCheck();
    }

    onYywwSearchChange(raw: string): void {
        const value = raw ?? '';
        this.yywwSearch = value;
        this.yywwSearch$.next(value);
        this.cdr.markForCheck();
    }

    private async loadInitialPreviousWeek(): Promise<void> {
        const currentYear = new Date().getFullYear();
        const term = String(currentYear);
        const activeSeq = ++this.yywwSeq;

        this.yywwSearch = term;
        this.yywwActiveTerm = term;
        this.yywwPage = 1;
        this.yywwHasMore = true;
        this.yywwResults = [];
        this.yywwIsLoading = true;
        this.selectedYyww = null;
        this.errorMessage = null;
        this.cdr.markForCheck();

        try {
            const weeks = await firstValueFrom(this.payrollSyncSvc.getAllYywwsByYear(currentYear));

            if (activeSeq !== this.yywwSeq) {
                return;
            }

            this.yywwResults = this.sortWeeks(weeks);
            this.yywwHasMore = false;

            const previousWeek = this.findPreviousWeek(this.yywwResults);
            this.selectedYyww = previousWeek ?? this.yywwResults[0] ?? null;

            if (this.selectedYyww) {
                this.yywwSearch = this.selectedYyww.fullYearWeek || this.selectedYyww.yearWeek;
            }
        } finally {
            if (activeSeq === this.yywwSeq) {
                this.yywwIsLoading = false;
                this.cdr.markForCheck();
            }
        }
    }

    private async yywwResetAndSearch(term: string, autoSelectPreviousWeek: boolean): Promise<void> {
        const activeSeq = ++this.yywwSeq;

        this.yywwActiveTerm = term;
        this.yywwPage = 1;
        this.yywwHasMore = true;
        this.yywwResults = [];
        this.yywwIsLoading = false;
        this.errorMessage = null;
        this.cdr.markForCheck();

        if (!term) {
            this.yywwHasMore = false;
            this.selectedYyww = null;
            this.cdr.markForCheck();
            return;
        }

        await this.yywwLoadNextPage(activeSeq);

        if (autoSelectPreviousWeek && activeSeq === this.yywwSeq) {
            this.autoSelectPreviousWeek();
        }
    }

    async yywwLoadNextPage(seq: number = this.yywwSeq): Promise<void> {
        if (seq !== this.yywwSeq) {
            return;
        }

        const term = this.yywwActiveTerm.trim();
        if (!term || this.yywwIsLoading || !this.yywwHasMore) {
            return;
        }

        this.yywwIsLoading = true;
        this.cdr.markForCheck();

        try {
            const response = await firstValueFrom(
                this.payrollSyncSvc
                    .getYywws({
                        searchTerm: term,
                        pageNumber: this.yywwPage,
                        pageSize: this.yywwPageSize,
                    })
                    .pipe(
                        catchError((error) => {
                            console.error('getYywws error', error);

                            const empty: ApiPagedResponse<YywwDto[]> = {
                                status: 'error',
                                totalCount: 0,
                                totalPages: 1,
                                currentPage: 1,
                                pageSize: this.yywwPageSize,
                                data: [],
                            };

                            return of(empty);
                        }),
                    ),
            );

            if (seq !== this.yywwSeq) {
                return;
            }

            const pageData = response?.data ?? [];
            const totalPages = Number(response?.totalPages ?? 1) || 1;

            this.yywwResults = this.sortWeeks([...this.yywwResults, ...pageData]);
            this.yywwHasMore = this.yywwPage < totalPages && pageData.length > 0;

            if (this.yywwHasMore) {
                this.yywwPage += 1;
            }
        } finally {
            if (seq === this.yywwSeq) {
                this.yywwIsLoading = false;
                this.cdr.markForCheck();
            }
        }
    }

    onYywwScroll(event: Event): void {
        const element = event.target as HTMLElement | null;
        if (!element) {
            return;
        }

        const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 120;
        if (nearBottom) {
            void this.yywwLoadNextPage();
        }
    }

    selectWeekFromDropdown(week: YywwDto): void {
        this.selectedYyww = week;
        this.yywwSearch = week.fullYearWeek || week.yearWeek;
        this.yywwOpen = false;
        this.cdr.markForCheck();
    }

    clearSelectedWeek(): void {
        this.selectedYyww = null;
        this.yywwSearch = String(new Date().getFullYear());
        this.yywwOpen = false;

        void this.loadInitialPreviousWeek();
        this.cdr.markForCheck();
    }

    get canSync(): boolean {
        return !!this.selectedYyww?.id && !this.isSyncing;
    }

    async startSync(): Promise<void> {
        const yywwId = this.selectedYyww?.id ?? null;

        if (!yywwId) {
            this.showToast('error', 'Select a payroll week.');
            return;
        }

        this.isSyncing = true;
        this.errorMessage = null;
        this.showToast('info', 'Payroll sync started…');
        this.cdr.markForCheck();

        try {
            await firstValueFrom(this.payrollSyncSvc.runPayableHoursByYywwId(yywwId));
            this.showToast('success', 'Payroll sync completed.');
        } catch (error) {
            console.error('runPayableHoursByYywwId error', error);
            this.showToast('error', 'Payroll sync failed.');
        } finally {
            this.isSyncing = false;
            this.cdr.markForCheck();
        }
    }

    get selectedWeekSummary(): string {
        if (!this.selectedYyww) {
            return '—';
        }

        const fullYearWeek = this.selectedYyww.fullYearWeek ?? this.selectedYyww.yearWeek ?? '';
        const range =
            this.selectedYyww.startDate && this.selectedYyww.endDate
                ? `${this.selectedYyww.startDate} → ${this.selectedYyww.endDate}`
                : '';

        return `${fullYearWeek}${range ? ` (${range})` : ''}`.trim();
    }

    clearToast(): void {
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }

        this.toastTimer = null;
        this.toastMessage = null;
        this.cdr.markForCheck();
    }

    private showToast(kind: ToastKind, message: string): void {
        this.toastKind = kind;
        this.toastMessage = message;

        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }

        this.toastTimer = setTimeout(() => this.clearToast(), 2600);
        this.cdr.markForCheck();
    }

    private autoSelectPreviousWeek(): void {
        const previousWeek = this.findPreviousWeek(this.yywwResults);

        this.selectedYyww = previousWeek ?? this.yywwResults[0] ?? null;

        if (this.selectedYyww) {
            this.yywwSearch = this.selectedYyww.fullYearWeek || this.selectedYyww.yearWeek;
        }

        this.cdr.markForCheck();
    }

    private findPreviousWeek(weeks: YywwDto[]): YywwDto | null {
        const lastWeekDate = new Date();
        lastWeekDate.setDate(lastWeekDate.getDate() - 7);
        lastWeekDate.setHours(0, 0, 0, 0);

        return (
            weeks.find((week) => {
                const startDate = new Date(`${week.startDate}T00:00:00`);
                const endDate = new Date(`${week.endDate}T23:59:59`);

                return startDate <= lastWeekDate && lastWeekDate <= endDate;
            }) ?? null
        );
    }

    private sortWeeks(weeks: YywwDto[]): YywwDto[] {
        return [...weeks].sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.week - b.week;
        });
    }

    trackById(_: number, item: { id: number }): number {
        return item.id;
    }
}